// The brand the page wears before anyone signs in (ZENITH.md §18, ADR-0065;
// work item #505). The server resolves it from the host the request arrived
// on, so this asks for it and nothing more — the hostname is never read here,
// and could not be trusted if it were: what makes a host mean a brand is a
// binding the server holds, and a page cannot check one.
//
// **This is not `Me.brand` and does not become it.** ADR-0065 decision 4 rules
// that the host brands only the pages before a session; after sign-in §18's
// membership brand wins, which is what `App` already applies. So a person
// under reseller R who signs in at `console.arpiq.ai` meets arpIQ's sign-in
// page and then sees R's shell, and that change at the moment of sign-in is
// the ruling rather than a flicker to fix.
import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { Brand } from "./brand";

/** What the host resolved to, as far as a page needs to know.
 *
 * `undefined` while the answer is outstanding; `null` when there is none —
 * which for this endpoint means the host is unbound and the server answered
 * 404 (decision 8). A page that meets `null` renders exactly what it renders
 * for the neutral home, because an unbound host must not be distinguishable
 * from any other by what it draws. In practice the sign-in page is not
 * reached at all on an unbound host: the server refuses the shell before the
 * bundle is served. */
export type HostBrand = Brand | null | undefined;

/** Asks the server, once, what brand this host wears.
 *
 * **A hook and not a provider, deliberately.** The only consumer is the
 * sign-in page, so a provider around the application would spend a request
 * per page load on a value every signed-in page then ignores — and the
 * read-budget tests that caught that are the reason this is written down
 * rather than left as a preference. Calling it from the one page that draws
 * the host's brand means the request happens exactly when there is no session
 * and never when there is. */
export function useHostBrand(): HostBrand {
  const [brand, setBrand] = useState<HostBrand>(undefined);
  useEffect(() => {
    let live = true;
    void (async () => {
      const { data } = await api.GET("/brand");
      if (live) setBrand(data ?? null);
    })();
    return () => { live = false; };
  }, []);
  return brand;
}
